import React, { useState } from "react";
import { useMutation } from "@apollo/react-hooks";
import Rating from '@mui/material/Rating';
import TextField from '@mui/material/TextField';

import './review-write-form-style.scss';

import Button from "@src/componentsNew/Buttons/Button";
import Typography from "@src/componentsNew/Typography/Typography";
import ReviewImageUpload from "@src/componentsNew/ImageUpload/ReviewImageUpload";

import StarIcon from "@src/componentsNew/Icons/Icons/StarIcon";

import {
  REVIEW_CREATE,
  REVIEW_IMAGES_CREATE,
} from "@organisms/WriteReview/queries";
import { reviewCreate } from "@organisms/WriteReview/__generated__/reviewCreate";
import { reviewImagesCreate } from "@organisms/WriteReview/__generated__/reviewImagesCreate";
import { product_product_variants } from "@components/ProductPage/ProductDetailPage/__generated__/product";

interface IProps {
  productId: string;
  productRefetch?: any;
  variants: (product_product_variants | null)[];
  showVariants: boolean;
}

const ReviewWriteForm = ({
  productId,
  productRefetch,
  variants,
  showVariants,
}: IProps) => {
  const [reviewRating, setReviewRating] = useState(5);

  const [reviewTitle, setReviewTitle] = useState("");
  const [reviewTitleError, setReviewTitleError] = useState(false);
  const [reviewTitleResultText, setReviewTitleResultText] = useState('');

  const [reviewComment, setReviewComment] = useState("");
  const [reviewCommentError, setReviewCommentError] = useState(false);
  const [reviewCommentResultText, setReviewCommentResultText] = useState('');

  const [reviewImages, setReviewImages] = useState([]);

  const [selectedVariant, setSelectedVariant] = useState<product_product_variants | null>(null);
  const [reviewVariantError, setReviewVariantError] = useState(false);
  const [reviewVariantResultText, setReviewVariantResultText] = useState('');


  const [createReview, { data, error, loading }] = useMutation<reviewCreate>(REVIEW_CREATE, {
    onCompleted: (data) => {
      if (reviewImages.length) {
        const reviewId = data.reviewCreate.productReview.id;
        const reviewImagesFileArr = reviewImages.map(image => image.file);

        createReviewImages({
          variables: {
            input: {
              review: reviewId,
              images: reviewImagesFileArr,
            },
          },
        });
      }

      productRefetch();
    },
  });

  const [createReviewImages, { data: createReviewImagesData, error: createReviewImagesError, loading: createReviewImagesLoading }] = useMutation<reviewImagesCreate>(REVIEW_IMAGES_CREATE, {
    onCompleted: (data) => {
      if (data.reviewImagesCreate.errors.length === 0) {
        setReviewImages([]);
      }
    },
  });

  const handleReviewRatingChange = (event, newValue) => {
    if (newValue) {
      setReviewRating(newValue);
    }
  };

  const handleClickVariant = (variant: product_product_variants) => {
    if (variant.id === selectedVariant?.id) {
      setSelectedVariant(null);
      setReviewVariantError(true);
      setReviewVariantResultText('Please select variant');
    } else {
      setSelectedVariant(variant);
      setReviewVariantError(false);
      setReviewVariantResultText('');
    }
  };

  const handleReviewTitleChange = (e) => {
    const newValue = e.target.value;

    if (newValue.length <= 51) {
      setReviewTitle(newValue);
      if (newValue.length === 0) {
        setReviewTitleResultText('Title must not be empty');
        setReviewTitleError(true);
      } else if (newValue.length < 10) {
        setReviewTitleResultText('Title must be 10 ~ 50 characters');
        setReviewTitleError(true);
      } else if (newValue.length === 51) {
        setReviewTitleResultText('Up to 50 characters can be entered');
        setReviewTitleError(true);
      } else {
        setReviewTitleResultText('');
        setReviewTitleError(false);
      }
    }
  };

  const handleReviewCommentChange = (e) => {
    const newValue = e.target.value;

    if (newValue.length <= 401) {
      setReviewComment(newValue);
      if (newValue.length === 0) {
        setReviewCommentResultText('Contents must not be empty');
        setReviewCommentError(true);
      } else if (newValue.length < 30) {
        setReviewCommentResultText('Contents must be 30 ~ 400 characters');
        setReviewCommentError(true);
      } else if (newValue.length === 401) {
        setReviewCommentResultText('Up to 400 characters can be entered');
        setReviewCommentError(true);
      } else {
        setReviewCommentResultText('');
        setReviewCommentError(false);
      }
    }
  };

  const handleClickSubmitButton = () => {
    if (showVariants && !selectedVariant) {
      setReviewVariantResultText('Please select variant');
      setReviewVariantError(true);
    }

    if (!reviewTitle) {
      setReviewTitleResultText('Title must not be empty');
      setReviewTitleError(true);
    }

    if (!reviewComment) {
      setReviewCommentResultText('Contents must not be empty');
      setReviewCommentError(true);
    }

    if ((showVariants && !selectedVariant) || !reviewTitle || !reviewComment) {
      return;
    }

    if (!reviewVariantError && !reviewTitleError && !reviewCommentError) {
      createReview({
        variables: {
          input: {
            product: productId,
            rating: reviewRating,
            title: reviewTitle,
            comment: reviewComment,
            variant: showVariants ? selectedVariant.id : null
          },
        },
      });
    }
  };

  return (
    <div id="review-write-form-container">
      <Typography variant="subtitle1" component="span" style={{ color: "#212121" }}>
        Write a Review
      </Typography>

      <Rating
        className="rating-area"
        size="medium"
        defaultValue={5}
        value={reviewRating}
        onChange={handleReviewRatingChange}
        icon={<StarIcon fillColor="#F82296" strokeColor="#F82296" />}
      />

      {
        showVariants ?
          <div className="variants-area">
            <Typography cName="title-result-text" variant="body3" component="span" style={{ color: "#212121" }}>
              {selectedVariant ? `${selectedVariant.name}` : "Select Variants"}
            </Typography>

            <ul className="variant-image-wrapper">
              {
                variants?.map(variant =>
                  <li key={variant.id}
                    className={`${variant.id === selectedVariant?.id ? "selected" : ''}`}
                    onClick={() => handleClickVariant(variant)}>
                    <img src={variant?.attributes[0]?.value?.image?.url} />
                  </li>
                )
              }
            </ul>
            {
              reviewVariantResultText ?
                <Typography cName="variant-result-text" variant="body3" component="p" style={{ color: "#FF3D00" }}>
                  {reviewVariantResultText}
                </Typography>
                : null
            }
          </div> : null
      }

      <div className="textarea-wrapper">
        <TextField
          size="small"
          className="title-input"
          label="Title"
          placeholder="Give your review a title"
          value={reviewTitle}
          multiline
          disabled={loading}
          onChange={handleReviewTitleChange}
          fullWidth
        />
        {
          reviewTitleResultText ?
            <Typography cName="title-result-text" variant="body3" component="p">
              {reviewTitleResultText}
            </Typography>
            : null
        }
      </div>

      <div className="textarea-wrapper">
        <TextField
          size="small"
          className="comment-input"
          label="Review"
          placeholder="Write your comments here"
          value={reviewComment}
          multiline
          disabled={loading}
          onChange={handleReviewCommentChange}
          fullWidth
        />
        {
          reviewCommentResultText ?
            <Typography cName="comment-result-text" variant="body3" component="p">
              {reviewCommentResultText}
            </Typography>
            : null
        }
      </div>

      <ReviewImageUpload
        images={reviewImages}
        setImages={setReviewImages}
        maxNumber={5}
      />

      <Button
        size="large"
        cName="submit-btn"
        variant="contained"
        onClick={handleClickSubmitButton}>
        Submit
      </Button>
    </div>
  );
};

export default ReviewWriteForm;
